import Box from "@mui/material/Box";
import { useCallback, useEffect, useState } from "react";
import { useGetPurchaseInvoiceService } from "subModule/src/services/inventory/useInventory";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import Loader from "layout/Loader";
import BackendPaginationTable from "common/Table/TableWithPagination";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Invoice } from "assets/Modal/invoice.svg";
import { useNavigate } from "react-router-dom";
import { Button, CircularProgress, SelectChangeEvent } from "@mui/material";
import PurchaseInvoiceSearchHeader from "components/Inventory/PurchaseInvoiceSearchHeader";
import { themes } from "theme/themes";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import { CommonProfileService } from "subModule/src/core/services";
import DropdownMenu from "common/DropDownMenu/DropDownMenu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
interface SearchFormData {
  [key: string]: string | any;
}
interface BillProps {
  supplierId?: number;
}
export default function PurchaseInvoiceListing(props: BillProps) {
  const navigate = useNavigate();
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(props?.supplierId ? 5 : 20);
  const [filter, setFilter] = useState<string[]>([
    "invoiceNumber",
    "referenceNo",
    "invoiceType",
  ]);
  const [search, setSearch] = useState<any>({});
  const [downloading, setDownloading] = useState<boolean>(false);
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    invoiceNumber: "",
    referenceNo: "",
    invoiceType: null,
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const {
    data: tableData,
    isFetching,
    refetch,
  } = useGetPurchaseInvoiceService({
    pageNumber: pageNo,
    size: pageSize,
    ...search,
    ...(props?.supplierId && { supplierId: props.supplierId }),
  });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const invoiceTypes = [
    { label: t("Purchase"), value: 1 },
    { label: t("expense"), value: 2 },
  ];
  const fetchData = useCallback(() => {
    refetch().then((res: any) => {
      if (!res?.data?.pages[0]?.success)
        enqueueSnackbar(
          <AlertMessages
            text={res?.data?.pages[0]?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
    });
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search]);
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const showRowView = (row: any) =>
    navigate("/inventory/supplier-bill/" + row.id + "/print", {
      state: { supplier: props?.supplierId || null },
    });
  const clearSearch = () => {
    setFilter(["invoiceNumber", "referenceNo", "invoiceType"]);
    setSearchFormData({
      invoiceNumber: "",
      referenceNo: "",
      invoiceType: null,
    });
    setSearch({});
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    setFilter(typeof value === "string" ? value.split(",") : value);
    setSearchFormData((prevFormData) =>
      Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          value.includes(key) ? prevFormData[key] : "",
        ]),
      ),
    );
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    const val =
      field === "invoiceType"
        ? value
          ? parseInt(value.value) || 0
          : 0
        : value;
    setSearchFormData((prevData) => ({
      ...prevData,
      [field]: val,
    }));
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string | number | boolean> = {};
    Object.entries(searchFormData).forEach(([key, value]) => {
      if (value) {
        filteredSearch[key] = typeof value === "string" ? value?.trim() : value;
      }
    });
    setSearch(filteredSearch);
    setPageNo(1);
    setPageSize(pageSize);
  };
  const DownloadPurchaseInvoices = (type: "pdf" | "excel") => {
    CommonProfileService.exportEntityWithParams(
      { ...search, ...(props?.supplierId && { supplierId: props.supplierId }) },
      type === "excel"
        ? "purchaseinvoice/export/excel"
        : "purchaseinvoice/export/pdf",
    )
      .then((res: Blob) => {
        const blob = new Blob([res], {
          type:
            type === "excel"
              ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              : "application/pdf",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          type === "excel" ? `SupplierBills.xlsx` : `SupplierBills.pdf`,
        );
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
        enqueueSnackbar(<AlertMessages text={t("downloadedSuccessfully")} />, {
          variant: "success",
        });
      })
      .catch((err) => {
        console.error("===", err);
        enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      })
      .finally(() => {
        setDownloading(false);
      });
  };
  return (
    <Box>
      {props?.supplierId ? null : (
        <ComponentHeaderTwo
          showBtn={true}
          showSaveBtn={false}
          onClick={() => navigate("/inventory/new-supplier-bill")}
          heading={t("supplierBills")}
          icon={
            <Invoice
              style={{
                fill: "#000",
                width: "30px",
                height: "30px",
                marginTop: "3px",
              }}
            />
          }
          btnText={"create"}
          btnType={"create"}
          showCreateBtn={checkPrivileges("ROLE_PURCHASEINVOICE_CREATE")}
        />
      )}
      <Box>
        {isFetching ? (
          <Loader />
        ) : (
          <div id="mainListingTable">
            <BackendPaginationTable
              columns={[
                {
                  id: "invoiceNumber",
                  label: "supplierBillNo",
                  numeric: false,
                  width: "12%",
                },
                {
                  id: "referenceNumber",
                  label: t("referenceNo"),
                  numeric: false,
                  width: "10%",
                },
                {
                  id: "invoiceType",
                  label: t("invoiceType"),
                  numeric: false,
                  width: "10%",
                },
                {
                  id: "invoiceDate",
                  label: "Date",
                  numeric: false,
                  width: "9%",
                },
                {
                  id: "vendorTitle",
                  label: "Supplier",
                  numeric: false,
                  width: "14%",
                },
                {
                  id: "stationStore",
                  label: "stationStore",
                  numeric: false,
                  width: "16%",
                },
                // {
                //   id: "subtotalAmount",
                //   label: "Subtotal",
                //   numeric: false,
                //   width: "8%",
                // },
                // {
                //   id: "discount",
                //   label: t("Discount"),
                //   numeric: false,
                //   width: "7%",
                // },
                {
                  id: "totalAmount",
                  label: t("Total"),
                  numeric: false,
                  width: "10%",
                },
                {
                  id: "paymentStatus",
                  label: t("paymentStatus"),
                  numeric: false,
                  width: "12%",
                },
              ]}
              data={tableData?.pages[0]?.data?.content || []}
              pageNo={pageNo}
              pageSize={pageSize}
              tableData={tableData?.pages[0]?.data || {}}
              showCheckbox={false}
              showActions={true}
              enableSorting={true}
              showHeader={true}
              showSearch={true}
              showFilter={true}
              showCreateButton={true}
              handleChangePage={handleChangePage}
              handleChangeRows={handleChangeRows}
              rowClick={checkPrivileges("ROLE_PURCHASEINVOICE_AUTHORIZE")}
              showRowView={showRowView}
              loadingSearch={false}
              headerComponent={
                <>
                  <Box className="table-header-search">
                    <PurchaseInvoiceSearchHeader
                      searchFormData={searchFormData}
                      handleChangeFilter={handleChangeFilter}
                      handleChangeSearch={handleChangeSearch}
                      handleSubmitSearch={handleSubmitSearch}
                      clearSearch={clearSearch}
                      {...{ filter, search, invoiceTypes }}
                    />
                  </Box>
                  {checkPrivileges("ROLE_PURCHASEINVOICE_EXPORT") ? (
                    <Box className="table-header-actions">
                      <Button
                        id="import-button"
                        variant="contained"
                        disableElevation
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                          setAnchorEl(event.currentTarget)
                        }
                        endIcon={<KeyboardArrowDownIcon />}
                        className={`bg-white font-Saira font-[600] text-[13px] px-4 py-1 cursor-pointer rounded-[6px]`}
                        sx={{
                          outline: "2px solid",
                          color: themeColors?.primary,
                          borderColor: themeColors?.primary,
                          height: 34,
                          minHeight: 34,
                        }}
                      >
                        {downloading ? (
                          <CircularProgress
                            sx={{
                              color: themeColors?.primary,
                              left: 0,
                              position: "absolute",
                            }}
                            size={16}
                          />
                        ) : null}{" "}
                        {t("export")}
                      </Button>
                      <DropdownMenu
                        anchorEl={anchorEl}
                        menuItems={[
                          {
                            label: t("downloadExcel"),
                            onClick: () => {
                              setDownloading(true);
                              DownloadPurchaseInvoices("excel");
                            },
                          },
                          {
                            label: t("downloadPDF"),
                            onClick: () => {
                              setDownloading(true);
                              DownloadPurchaseInvoices("pdf");
                            },
                          },
                        ]}
                        onClose={() => setAnchorEl(null)}
                        hoverColor={themeColors?.primary}
                      />
                    </Box>
                  ) : null}
                </>
              }
            />
          </div>
        )}
      </Box>
    </Box>
  );
}
